import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '../../../core/components/Alert.tsx';

interface ProductionOutputNoticeProps {
    /** The owner's "Use Output Mapper" switch; null/undefined when the owner has none. */
    isActive?: boolean | null;
    /** A channel's switch lives in its Output File settings, an integration's in the integration settings. */
    ownerKind: 'channel' | 'integration';
}

/**
 * Header notice of the Output Studio: a warning while "Use Output Mapper" is off (the mapping only drives test
 * runs and the standard output is sent), and a short reminder once it is on.
 */
const ProductionOutputNotice = ({ isActive, ownerKind }: ProductionOutputNoticeProps): React.ReactElement | null => {
    const { t } = useTranslation('integrations');
    if (isActive === null || isActive === undefined) return null;
    if (!isActive) {
        return (
            <Alert severity="warning">
                {ownerKind === 'channel'
                    ? t(
                          'outputMapper.notActiveChannel',
                          'Output Studio is not active for this channel. Enable "Use Output Mapper" in the channel\'s Output File settings — until then the channel sends its standard output file and this configuration is only used for test runs.'
                      )
                    : t(
                          'outputMapper.notActiveIntegration',
                          'Output Studio is not active for this integration. Enable "Use Output Mapper" in the integration settings — until then the standard payload is sent and this configuration is only used for test runs.'
                      )}
            </Alert>
        );
    }
    return (
        <Alert severity="info">
            {t(
                'outputMapper.productionOutputNotice',
                'Output Studio is active: this saved configuration generates the output that is sent. Test changes before saving.'
            )}
        </Alert>
    );
};

export default ProductionOutputNotice;
